import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { format, parseISO, subDays, eachDayOfInterval, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSelector } from 'react-redux';
import '../analytics/SessionEvolutionChart.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const SessionEvolutionChartBackend = ({ selectedPeriod = '7 derniers jours', email = '' }) => {
  const { allSessions } = useSelector(s => s.sessions || { allSessions: [] });
  const [chartData, setChartData] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'total', 'activity', 'inactivity'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    buildChartData();
  }, [selectedPeriod, viewMode, email, allSessions]);

  const buildChartData = () => {
    setLoading(true);
    try {
      const sessions = (allSessions || []).filter(s => s.startedAt && s.endedAt).filter(s => {
        const e = (s.email || s.userId || '').toLowerCase();
        return email ? e.includes(email.toLowerCase()) : true;
      });

      const endDate = new Date();
      let startDate;
      switch(selectedPeriod) {
        case "Aujourd'hui": startDate = startOfDay(endDate); break;
        case '7 derniers jours': startDate = subDays(endDate, 6); break;
        case '30 derniers jours': startDate = subDays(endDate, 29); break;
        case '3 derniers mois': startDate = subDays(endDate, 89); break;
        case '1 dernière année': startDate = subDays(endDate, 364); break;
        default: startDate = subDays(endDate, 6);
      }

      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      const dailyData = {};
      allDays.forEach(day => {
        const key = format(day, 'yyyy-MM-dd');
        dailyData[key] = { date: day, total: 0, activity: 0, inactivity: 0, sessionCount: 0 };
      });

      sessions.forEach(session => {
        const sessionDate = format(parseISO(session.startedAt), 'yyyy-MM-dd');
        if (dailyData[sessionDate]) {
          const totalDuration = (parseISO(session.endedAt).getTime() - parseISO(session.startedAt).getTime()) / (60 * 1000);
          dailyData[sessionDate].total += totalDuration;
          dailyData[sessionDate].activity += session.activity_duration || 0;
          dailyData[sessionDate].inactivity += session.inactivity_duration || 0;
          dailyData[sessionDate].sessionCount += 1;
        }
      });

      const sortedDays = Object.values(dailyData).sort((a, b) => a.date - b.date);

      const labels = sortedDays.map(day => {
        if (selectedPeriod === "Aujourd'hui") {
          return format(day.date, 'HH:mm', { locale: fr });
        } else if (selectedPeriod === '7 derniers jours') {
          return format(day.date, 'EEE d', { locale: fr });
        } else if (selectedPeriod === '30 derniers jours') {
          return format(day.date, 'd MMM', { locale: fr });
        } else {
          return format(day.date, 'MMM yyyy', { locale: fr });
        }
      });

      const datasets = [];
      if (viewMode === 'all' || viewMode === 'total') {
        datasets.push({
          label: 'Temps total',
          data: sortedDays.map(day => Math.round(day.total)),
          borderColor: '#22543D', // Vert foncé pour le temps total
          backgroundColor: 'rgba(34, 84, 61, 0.1)',
          tension: 0.4,
          fill: viewMode === 'total',
          pointRadius: 4,
          pointHoverRadius: 6,
        });
      }
      if (viewMode === 'all' || viewMode === 'activity') {
        datasets.push({
          label: "Temps d'activité",
          data: sortedDays.map(day => Math.round(day.activity)),
          borderColor: '#38A169', // Vert moyen pour l'activité
          backgroundColor: 'rgba(56, 161, 105, 0.15)',
          tension: 0.4,
          fill: viewMode === 'activity',
          pointRadius: 4,
          pointHoverRadius: 6,
        });
      }
      if (viewMode === 'all' || viewMode === 'inactivity') {
        datasets.push({
          label: "Temps d'inactivité",
          data: sortedDays.map(day => Math.round(day.inactivity)),
          borderColor: '#68D391', // Vert clair pour l'inactivité
          backgroundColor: 'rgba(104, 211, 145, 0.1)',
          tension: 0.4,
          fill: viewMode === 'inactivity',
          pointRadius: 4,
          pointHoverRadius: 6,
        });
      }

      setChartData({ labels, datasets });
    } catch (error) {
      console.error('Erreur SessionEvolutionChartBackend:', error);
    } finally {
      setLoading(false);
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15, /* Réduit de 20 à 15 */
          font: { size: 11, /* Réduit de 12 à 11 */ weight: '500' }
        }
      },
      title: {
        display: true,
        text: `Évolution des durées de session - ${selectedPeriod}`,
        font: { size: 14, /* Réduit de 16 à 14 */ weight: '600' },
        padding: { bottom: 15 /* Réduit de 20 à 15 */ }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            const hours = Math.floor(value / 60);
            const minutes = value % 60;
            let formattedValue;
            if (hours > 0) {
              formattedValue = `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`;
            } else {
              formattedValue = `${minutes}min`;
            }
            return `${context.dataset.label}: ${formattedValue}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { maxRotation: 45, font: { size: 10 /* Réduit de 11 à 10 */ } }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            if (value === 0) return '0min';
            const hours = Math.floor(value / 60);
            const minutes = value % 60;
            if (hours > 0) {
              return `${hours}h${minutes > 0 ? `${minutes}m` : ''}`;
            }
            return `${minutes}min`;
          },
          font: { size: 10 /* Réduit de 11 à 10 */ }
        }
      }
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false }
  };

  if (loading) {
    return (
      <div className="session-evolution-chart">
        <div className="chart-loading">
          <div className="spinner">Chargement du graphique...</div>
        </div>
      </div>
    );
  }

  if (!chartData || chartData.datasets.length === 0) {
    return (
      <div className="session-evolution-chart">
        <div className="chart-empty">
          <p>Aucune donnée disponible pour cette période</p>
        </div>
      </div>
    );
  }

  return (
    <div className="session-evolution-chart">
      <div className="chart-view-filter">
        <div className="filter-options">
          <button className={`filter-option ${viewMode === 'all' ? 'active' : ''}`} onClick={() => setViewMode('all')}>
            Toutes les courbes
          </button>
          <button className={`filter-option ${viewMode === 'total' ? 'active' : ''}`} onClick={() => setViewMode('total')}>
            Temps total
          </button>
          <button className={`filter-option ${viewMode === 'activity' ? 'active' : ''}`} onClick={() => setViewMode('activity')}>
            Activité
          </button>
          <button className={`filter-option ${viewMode === 'inactivity' ? 'active' : ''}`} onClick={() => setViewMode('inactivity')}>
            Inactivité
          </button>
        </div>
      </div>
      <div className="chart-container">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default SessionEvolutionChartBackend;
